import {
  Body,
  ConflictException,
  Controller,
  Delete,
  Get,
  Injectable,
  Module,
  NotFoundException,
  Param,
  Patch,
  Post,
  Query,
} from '@nestjs/common';
import {
  ApiBearerAuth,
  ApiProperty,
  ApiTags,
  PartialType,
} from '@nestjs/swagger';
import {
  IsBoolean,
  IsDateString,
  IsEmail,
  IsEnum,
  IsInt,
  IsNumber,
  IsOptional,
  IsString,
  IsUUID,
  Matches,
  MaxLength,
  Min,
  MinLength,
} from 'class-validator';
import { hash } from 'argon2';
import { createHash } from 'node:crypto';
import { Prisma } from '@sakura/prisma';
import { ProductStatus, Role } from '@sakura/prisma/enums';
import { PrismaService } from '../prisma/prisma.module';
import { Permissions, Roles } from '../common/security';

function cleanCatalogValue(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

function medicineCatalogSourceKey(
  productName: string,
  companyName: string,
  genericName: string,
): string {
  return createHash('sha256')
    .update(
      [productName, companyName, genericName]
        .map((value) => cleanCatalogValue(value).toLocaleLowerCase())
        .join('\u0000'),
    )
    .digest('hex');
}

class CreateUserDto {
  @ApiProperty()
  @IsString()
  @MinLength(3)
  @MaxLength(60)
  @Matches(/^[a-zA-Z0-9._-]+$/)
  username!: string;
  @ApiProperty()
  @IsString()
  @MinLength(2)
  @MaxLength(160)
  fullName!: string;
  @ApiProperty()
  @IsString()
  @MinLength(2)
  @MaxLength(60)
  employeeNo!: string;
  @ApiProperty()
  @IsString()
  @MinLength(7)
  @MaxLength(30)
  contactNo!: string;
  @ApiProperty()
  @IsString()
  @MinLength(8)
  password!: string;
  @ApiProperty({ enum: Role })
  @IsEnum(Role)
  role!: Role;
  @IsOptional()
  @IsUUID()
  branchId?: string;
}

class UpdateUserDto {
  @IsOptional()
  @IsString()
  username?: string;
  @IsOptional()
  @IsString()
  employeeNo?: string;
  @IsOptional()
  @IsString()
  contactNo?: string;
  @IsOptional()
  @IsString()
  firstName?: string;
  @IsOptional()
  @IsString()
  lastName?: string;
  @IsOptional()
  @IsEnum(Role)
  role?: Role;
  @IsOptional()
  @IsBoolean()
  isActive?: boolean;
  @IsOptional()
  @IsUUID()
  branchId?: string;
}

@Injectable()
class UsersService {
  constructor(private readonly prisma: PrismaService) {}
  list() {
    return this.prisma.user.findMany({ omit: { passwordHash: true } });
  }
  get(id: string) {
    return this.prisma.user.findUniqueOrThrow({
      where: { id },
      omit: { passwordHash: true },
    });
  }
  async create(dto: CreateUserDto) {
    const username = dto.username.trim().toLowerCase();
    const employeeNo = dto.employeeNo.trim().toUpperCase();
    const existing = await this.prisma.user.findFirst({
      where: { OR: [{ username }, { employeeNo }] },
      select: { username: true, employeeNo: true },
    });
    if (existing?.username === username) {
      throw new ConflictException('Username is already in use');
    }
    if (existing?.employeeNo === employeeNo) {
      throw new ConflictException('Employee number is already in use');
    }
    const nameParts = dto.fullName.trim().split(/\s+/);
    const firstName = nameParts.shift() ?? dto.fullName.trim();
    const lastName = nameParts.join(' ') || '-';
    return this.prisma.user.create({
      data: {
        username,
        employeeNo,
        contactNo: dto.contactNo.trim(),
        email: `${username}@sakurapharma.local`,
        firstName,
        lastName,
        role: dto.role,
        branchId: dto.branchId,
        passwordHash: await hash(dto.password),
      },
      omit: { passwordHash: true },
    });
  }
  update(id: string, data: UpdateUserDto) {
    return this.prisma.user.update({
      where: { id },
      data,
      omit: { passwordHash: true },
    });
  }
}

@ApiTags('users')
@ApiBearerAuth()
@Roles(Role.SUPER_ADMIN)
@Controller('users')
class UsersController {
  constructor(private readonly service: UsersService) {}
  @Get()
  list() {
    return this.service.list();
  }
  @Get(':id')
  get(@Param('id') id: string) {
    return this.service.get(id);
  }
  @Post()
  create(@Body() dto: CreateUserDto) {
    return this.service.create(dto);
  }
  @Patch(':id')
  update(@Param('id') id: string, @Body() dto: UpdateUserDto) {
    return this.service.update(id, dto);
  }
}

class CompanyDto {
  @ApiProperty({ required: false })
  @IsOptional()
  @IsString()
  code?: string;
  @ApiProperty()
  @IsString()
  name!: string;
  @IsOptional()
  @IsString()
  contactName?: string;
  @IsOptional()
  @IsString()
  phone?: string;
  @IsOptional()
  @IsEmail()
  email?: string;
  @IsOptional()
  @IsString()
  address?: string;
  @IsOptional()
  @IsString()
  vatNumber?: string;
  @IsOptional()
  @IsNumber()
  @Min(0)
  vatRate?: number;
  @IsOptional()
  @IsNumber()
  @Min(0)
  commission?: number;
  @IsOptional()
  @IsString()
  department?: string;
  @IsOptional()
  @IsBoolean()
  isActive?: boolean;
}
class UpdateCompanyDto extends PartialType(CompanyDto) {}

@ApiTags('companies')
@ApiBearerAuth()
@Permissions('companies.view')
@Controller('companies')
class CompaniesController {
  constructor(private readonly prisma: PrismaService) {}
  @Get()
  list(@Query('q') query = '', @Query('take') requestedTake = '1000') {
    const q = query.trim();
    const take = Math.min(Math.max(Number(requestedTake) || 1000, 1), 1000);
    return this.prisma.company.findMany({
      where: q
        ? {
            OR: [
              { name: { contains: q, mode: 'insensitive' } },
              { phone: { contains: q, mode: 'insensitive' } },
              { address: { contains: q, mode: 'insensitive' } },
            ],
          }
        : undefined,
      orderBy: { name: 'asc' },
      take,
    });
  }
  @Get(':id')
  get(@Param('id') id: string) {
    return this.prisma.company.findUniqueOrThrow({ where: { id } });
  }
  @Post()
  async create(@Body() data: CompanyDto) {
    const name = data.name.trim();
    const existing = await this.prisma.company.findFirst({
      where: { name: { equals: name, mode: 'insensitive' } },
      select: { id: true },
    });
    if (existing) {
      throw new ConflictException('A company with this name already exists');
    }
    return this.prisma.company.create({
      data: {
        ...data,
        name,
        code:
          data.code?.trim() ?? `CMP-${Date.now().toString(36).toUpperCase()}`,
      },
    });
  }
  @Patch(':id')
  update(@Param('id') id: string, @Body() data: UpdateCompanyDto) {
    return this.prisma.company.update({ where: { id }, data });
  }
}

@ApiTags('branches')
@ApiBearerAuth()
@Controller('branches')
class BranchesController {
  constructor(private readonly prisma: PrismaService) {}

  @Get()
  list() {
    return this.prisma.branch.findMany({
      where: { isActive: true },
      orderBy: { name: 'asc' },
    });
  }
}

@ApiTags('product-groups')
@ApiBearerAuth()
@Permissions('products.view')
@Controller('product-groups')
class ProductGroupsController {
  constructor(private readonly prisma: PrismaService) {}

  @Get()
  list(@Query('q') query = '') {
    const q = query.trim();
    return this.prisma.category.findMany({
      where: q
        ? {
            name: {
              contains: q,
              mode: 'insensitive',
            },
          }
        : undefined,
      orderBy: { name: 'asc' },
    });
  }
}

@ApiTags('generic-medicines')
@ApiBearerAuth()
@Permissions('products.view')
@Controller('generic-medicines')
class GenericMedicinesController {
  constructor(private readonly prisma: PrismaService) {}

  @Get()
  list(@Query('q') query = '', @Query('take') requestedTake = '2000') {
    const q = query.trim();
    const take = Math.min(Math.max(Number(requestedTake) || 2000, 1), 5000);
    return this.prisma.genericMedicine.findMany({
      where: q
        ? {
            OR: [
              { name: { contains: q, mode: 'insensitive' } },
              {
                therapeuticClass: {
                  contains: q,
                  mode: 'insensitive',
                },
              },
            ],
          }
        : undefined,
      orderBy: { name: 'asc' },
      take,
    });
  }
}

class MedicineCatalogDto {
  @ApiProperty()
  @IsString()
  @MinLength(1)
  @MaxLength(200)
  productName!: string;

  @ApiProperty()
  @IsString()
  @MinLength(1)
  @MaxLength(160)
  companyName!: string;

  @ApiProperty()
  @IsString()
  @MinLength(1)
  @MaxLength(200)
  genericName!: string;

  @IsOptional()
  @IsUUID()
  companyId?: string;
}

@Injectable()
class MedicineCatalogService {
  constructor(private readonly prisma: PrismaService) {}

  async list(query = '', requestedTake = '20') {
    const q = cleanCatalogValue(query);
    const take = Math.min(Math.max(Number(requestedTake) || 20, 1), 50);
    if (!q) {
      return this.prisma.medicineCatalogItem.findMany({
        orderBy: [{ productName: 'asc' }, { companyName: 'asc' }],
        take,
      });
    }

    const rows = await this.prisma.medicineCatalogItem.findMany({
      where: {
        OR: [
          { productName: { contains: q, mode: 'insensitive' } },
          { companyName: { contains: q, mode: 'insensitive' } },
          { genericName: { contains: q, mode: 'insensitive' } },
        ],
      },
      take: Math.max(take * 20, 200),
    });

    const needle = q.toLocaleLowerCase();
    const rank = (value: string): number => {
      const text = value.toLocaleLowerCase();
      if (text === needle) return 0;
      if (text.startsWith(needle)) return 1;
      if (text.includes(` ${needle}`)) return 2;
      if (text.includes(needle)) return 3;
      return 4;
    };

    return rows
      .map((row) => ({
        row,
        score: Math.min(
          rank(row.productName),
          rank(row.companyName) + 5,
          rank(row.genericName) + 5,
        ),
      }))
      .sort((left, right) => {
        if (left.score !== right.score) {
          return left.score - right.score;
        }
        const byName = left.row.productName.localeCompare(
          right.row.productName,
          undefined,
          { sensitivity: 'base' },
        );
        if (byName !== 0) {
          return byName;
        }
        return left.row.companyName.localeCompare(
          right.row.companyName,
          undefined,
          {
            sensitivity: 'base',
          },
        );
      })
      .slice(0, take)
      .map((entry) => entry.row);
  }

  create(dto: MedicineCatalogDto) {
    const productName = cleanCatalogValue(dto.productName);
    const companyName = cleanCatalogValue(dto.companyName);
    const genericName = cleanCatalogValue(dto.genericName);
    const sourceKey = medicineCatalogSourceKey(
      productName,
      companyName,
      genericName,
    );
    return this.prisma.medicineCatalogItem.upsert({
      where: { sourceKey },
      update: {
        productName,
        companyName,
        genericName,
        companyId: dto.companyId,
      },
      create: {
        productName,
        companyName,
        genericName,
        companyId: dto.companyId,
        sourceKey,
      },
    });
  }
}

@ApiTags('medicine-catalog')
@ApiBearerAuth()
@Permissions('products.view')
@Controller('medicine-catalog')
class MedicineCatalogController {
  constructor(private readonly service: MedicineCatalogService) {}

  @Get()
  list(@Query('q') query = '', @Query('take') take = '20') {
    return this.service.list(query, take);
  }

  @Post()
  create(@Body() dto: MedicineCatalogDto) {
    return this.service.create(dto);
  }
}

class ProductDto {
  @ApiProperty()
  @IsString()
  sku!: string;
  @ApiProperty()
  @IsString()
  genericName!: string;
  @ApiProperty()
  @IsString()
  unit!: string;
  @ApiProperty()
  @IsNumber()
  @Min(0)
  salePrice!: number;
  @ApiProperty()
  @IsNumber()
  @Min(0)
  purchasePrice!: number;
  @IsOptional()
  @IsString()
  barcode?: string;
  @IsOptional()
  @IsString()
  brandName?: string;
  @IsOptional()
  @IsUUID()
  companyId?: string;
  @IsOptional()
  @IsUUID()
  categoryId?: string;
  @IsOptional()
  @IsEnum(ProductStatus)
  status?: ProductStatus;
  @IsOptional()
  @IsString()
  therapeuticClass?: string;
  @IsOptional()
  @IsString()
  dosageForm?: string;
  @IsOptional()
  @IsString()
  strength?: string;
  @IsOptional()
  @IsInt()
  @Min(0)
  reorderLevel?: number;
  @IsOptional()
  @IsNumber()
  @Min(0)
  vatRate?: number;
  @IsOptional()
  @IsNumber()
  @Min(0)
  maxDiscountRate?: number;
  @IsOptional()
  @IsBoolean()
  prescriptionOnly?: boolean;
  @IsOptional()
  @IsInt()
  @Min(0)
  openingStock?: number;
  @IsOptional()
  @IsDateString()
  expiryDate?: string;
  @IsOptional()
  @IsUUID()
  branchId?: string;
}
class UpdateProductDto extends PartialType(ProductDto) {}

@ApiTags('products')
@ApiBearerAuth()
@Permissions('products.view')
@Controller('products')
class ProductsController {
  constructor(private readonly prisma: PrismaService) {}
  @Get()
  list() {
    return this.prisma.product.findMany({
      where: { deletedAt: null },
      include: {
        company: true,
        category: true,
        batches: { orderBy: { expiryDate: 'asc' } },
      },
    });
  }

  /** Exact barcode (or SKU) lookup for scanner guns. Must stay above `:id`. */
  @Get('by-barcode/:barcode')
  async getByBarcode(@Param('barcode') barcode: string) {
    const code = decodeURIComponent(barcode).trim();
    if (!code) {
      throw new NotFoundException('Barcode is required');
    }
    const product = await this.prisma.product.findFirst({
      where: {
        deletedAt: null,
        OR: [{ barcode: code }, { sku: code }],
      },
      include: {
        company: true,
        category: true,
        batches: { orderBy: { expiryDate: 'asc' } },
      },
    });
    if (!product) {
      throw new NotFoundException(`No product found for barcode "${code}"`);
    }
    return product;
  }

  @Get(':id')
  get(@Param('id') id: string) {
    return this.prisma.product.findFirstOrThrow({
      where: { id, deletedAt: null },
      include: { batches: true },
    });
  }
  @Post()
  create(@Body() dto: ProductDto) {
    const { openingStock, expiryDate, branchId, ...data } = dto;
    return this.prisma.$transaction(async (tx) => {
      const product = await tx.product.create({
        data: {
          ...data,
          batches:
            openingStock && branchId && expiryDate
              ? {
                  create: {
                    branchId,
                    batchNumber: `OPEN-${Date.now().toString(36)}`,
                    expiryDate: new Date(expiryDate),
                    quantity: openingStock,
                    purchasePrice: data.purchasePrice,
                    salePrice: data.salePrice,
                  },
                }
              : undefined,
        },
        include: { company: true, category: true, batches: true },
      });
      await this.syncMedicineCatalog(tx, product);
      return product;
    });
  }
  @Patch(':id')
  update(@Param('id') id: string, @Body() dto: UpdateProductDto) {
    const { openingStock, expiryDate, branchId, ...data } = dto;
    return this.prisma.$transaction(async (tx) => {
      const product = await tx.product.update({ where: { id }, data });
      await this.syncMedicineCatalog(tx, product);
      if (openingStock !== undefined && branchId) {
        const batches = await tx.productBatch.findMany({
          where: { productId: id, branchId },
          orderBy: { expiryDate: 'asc' },
        });
        if (batches[0]) {
          await tx.productBatch.update({
            where: { id: batches[0].id },
            data: {
              quantity: openingStock,
              expiryDate: expiryDate ? new Date(expiryDate) : undefined,
              purchasePrice: product.purchasePrice,
              salePrice: product.salePrice,
            },
          });
          if (batches.length > 1) {
            await tx.productBatch.updateMany({
              where: { id: { in: batches.slice(1).map((batch) => batch.id) } },
              data: { quantity: 0 },
            });
          }
        } else if (openingStock > 0 && expiryDate) {
          await tx.productBatch.create({
            data: {
              productId: id,
              branchId,
              batchNumber: `OPEN-${Date.now().toString(36)}`,
              expiryDate: new Date(expiryDate),
              quantity: openingStock,
              purchasePrice: product.purchasePrice,
              salePrice: product.salePrice,
            },
          });
        }
      }
      return tx.product.findUniqueOrThrow({
        where: { id },
        include: { company: true, category: true, batches: true },
      });
    });
  }
  @Delete(':id')
  remove(@Param('id') id: string) {
    return this.prisma.product.update({
      where: { id },
      data: { deletedAt: new Date() },
    });
  }

  private async syncMedicineCatalog(
    tx: Prisma.TransactionClient,
    product: {
      brandName: string | null;
      genericName: string;
      companyId: string | null;
    },
  ): Promise<void> {
    if (!product.brandName || !product.companyId) {
      return;
    }
    const company = await tx.company.findUnique({
      where: { id: product.companyId },
      select: { name: true },
    });
    if (!company) {
      return;
    }
    const productName = cleanCatalogValue(product.brandName);
    const companyName = cleanCatalogValue(company.name);
    const genericName = cleanCatalogValue(product.genericName);
    const sourceKey = medicineCatalogSourceKey(
      productName,
      companyName,
      genericName,
    );
    await tx.medicineCatalogItem.upsert({
      where: { sourceKey },
      update: {
        productName,
        companyName,
        genericName,
        companyId: product.companyId,
      },
      create: {
        productName,
        companyName,
        genericName,
        companyId: product.companyId,
        sourceKey,
      },
    });
  }
}

@ApiTags('inventory')
@ApiBearerAuth()
@Permissions('products.view')
@Controller('inventory')
class InventoryController {
  constructor(private readonly prisma: PrismaService) {}
  @Get('branch/:branchId')
  stock(@Param('branchId') branchId: string) {
    return this.prisma.productBatch.findMany({
      where: { branchId },
      include: { product: true },
      orderBy: { expiryDate: 'asc' },
    });
  }
  @Get('movements')
  movements() {
    return this.prisma.stockMovement.findMany({
      take: 200,
      orderBy: { createdAt: 'desc' },
      include: { product: true, branch: true },
    });
  }
}

@Module({
  controllers: [
    UsersController,
    CompaniesController,
    BranchesController,
    ProductGroupsController,
    GenericMedicinesController,
    MedicineCatalogController,
    ProductsController,
    InventoryController,
  ],
  providers: [UsersService, MedicineCatalogService],
})
export class CatalogModule {}
